import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FaChalkboardTeacher, FaChartLine } from 'react-icons/fa';
import api, { errorMessage as readError, saveSession } from '../api';
import dreamLogo from '../assets/dream.jpeg';

const Home = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSignIn = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await api.post('auth/login/', {
        username,
        password
      });

      saveSession(response.data);

      setErrorMessage('');
      navigate('/dashboard');
    } catch (error) {
      setErrorMessage(readError(error, 'Invalid username or password'));
    } finally {
      setIsLoading(false);
    }
  };

  const collageImages = [
    'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=900&q=80',
    'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=900&q=80',
    'https://images.unsplash.com/photo-1544717305-2782549b5136?auto=format&fit=crop&w=900&q=80'
  ];

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-[0_25px_80px_rgba(15,23,42,0.12)]">
        <div className="grid min-h-[760px] lg:grid-cols-[1.08fr_0.92fr]">
          <div className="relative hidden overflow-hidden bg-gradient-to-br from-emerald-900 via-green-800 to-slate-900 p-10 lg:flex lg:flex-col">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.32),transparent_32%),radial-gradient(circle_at_bottom_right,_rgba(14,165,233,0.18),transparent_28%)]" />

            <div className="relative z-10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl overflow-hidden border border-white/20 shadow-lg">
                  <img src={dreamLogo} alt="Dreamland College" className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.28em] text-emerald-100/80">Dreamland</p>
                  <h1 className="text-xl font-semibold text-white">College</h1>
                </div>
              </div>
              <div className="rounded-full border border-emerald-300/30 bg-emerald-400/10 px-3 py-1 text-xs font-medium text-emerald-100">
                Smart campus
              </div>
            </div>

            <div className="relative z-10 mt-12">
              <p className="text-sm font-medium uppercase tracking-[0.28em] text-emerald-200/80">Academic operations</p>
              <h2 className="mt-4 max-w-md text-4xl font-bold leading-tight text-white">
                Empowering education through better attendance.
              </h2>
              <p className="mt-4 max-w-md text-base text-slate-200/80">
                Manage classes, track staff performance, and keep your campus operations organized in a single digital workflow.
              </p>
            </div>

            <div className="relative z-10 mt-8 grid max-w-lg grid-cols-3 gap-4">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm">
                <FaChalkboardTeacher className="text-emerald-300 text-xl" />
                <p className="mt-3 text-2xl font-bold text-white">180+</p>
                <p className="text-xs text-slate-200/75">Classes</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm">
                <FaChartLine className="text-emerald-300 text-xl" />
                <p className="mt-3 text-2xl font-bold text-white">96%</p>
                <p className="text-xs text-slate-200/75">Engagement</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm">
                <div className="h-6 w-6 rounded-full border-2 border-emerald-300 border-t-transparent" />
                <p className="mt-3 text-2xl font-bold text-white">24/7</p>
                <p className="text-xs text-slate-200/75">Monitoring</p>
              </div>
            </div>

            <div className="relative z-10 mt-auto">
              <div className="grid grid-cols-[1.1fr_0.9fr] gap-4">
                <div className="overflow-hidden rounded-[26px] border border-white/10 bg-white/5 shadow-2xl backdrop-blur-sm">
                  <img src={collageImages[0]} alt="Campus collaboration" className="h-64 w-full object-cover" />
                </div>
                <div className="space-y-4">
                  <div className="overflow-hidden rounded-[22px] border border-white/10 bg-white/5 shadow-xl backdrop-blur-sm">
                    <img src={collageImages[1]} alt="Students and faculty" className="h-28 w-full object-cover" />
                  </div>
                  <div className="overflow-hidden rounded-[22px] border border-white/10 bg-white/5 shadow-xl backdrop-blur-sm">
                    <img src={collageImages[2]} alt="Education leadership" className="h-28 w-full object-cover" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center bg-slate-50 p-6 sm:p-8 lg:p-12">
            <div className="w-full max-w-md">
              <div className="mb-8 flex items-center justify-center lg:justify-start">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/30">
                  <img src={dreamLogo} alt="Logo" className="h-10 w-10 rounded-xl object-cover" />
                </div>
              </div>

              <div className="text-center lg:text-left">
                <p className="text-sm font-semibold uppercase tracking-[0.3em] text-emerald-600">Welcome back</p>
                <h3 className="mt-3 text-4xl font-bold tracking-tight text-slate-900">Sign in</h3>
                <p className="mt-3 text-base text-slate-500">
                  Access your academic portal and manage attendance with confidence.
                </p>
              </div>

              <form onSubmit={handleSignIn} className="mt-8 space-y-5">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Username</label>
                  <input
                    type="text"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-900 shadow-sm transition-all duration-200 placeholder:text-slate-400 focus:border-emerald-400 focus:outline-none focus:ring-4 focus:ring-emerald-100"
                    placeholder="Enter your username"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Password</label>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-900 shadow-sm transition-all duration-200 placeholder:text-slate-400 focus:border-emerald-400 focus:outline-none focus:ring-4 focus:ring-emerald-100"
                    placeholder="Enter your password"
                  />
                </div>

                {errorMessage && (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                    {errorMessage}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-4 text-sm font-semibold text-white shadow-lg shadow-emerald-600/20 transition-all duration-200 hover:from-emerald-500 hover:to-teal-500 focus:outline-none focus:ring-4 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:opacity-80"
                >
                  {isLoading ? (
                    <div className="flex items-center gap-3">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white"></div>
                      <span>Signing in...</span>
                    </div>
                  ) : (
                    <span>Sign In</span>
                  )}
                </button>
              </form>

              <div className="mt-8 flex items-center justify-center gap-2 text-sm text-slate-500">
                <span>Need an account?</span>
                <Link to="/register" className="font-semibold text-emerald-600 transition-colors hover:text-emerald-700">
                  Request access
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
